import { API_BASE_URL } from "../../config/apiConfig";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BriefcaseMedical,
  CheckCircle2,
  CirclePlus,
  Edit3,
  Layers3,
  Power,
  RefreshCw,
  Search,
  Stethoscope,
  X,
} from "lucide-react";

import PageLoader from "../../components/common/PageLoader";
import "./MasterDataManagement.css";

const API_BASE = API_BASE_URL;
const MASTER_DATA_URL = `${API_BASE}/master-data`;

const EMPTY_PROFESSION = {
  id: null,
  name: "",
  code: "",
  isActive: true,
};

const EMPTY_SPECIALTY = {
  id: null,
  professionId: "",
  name: "",
  displayOrder: "1",
  isActive: true,
};

const getToken = () =>
  localStorage.getItem("zethAuthToken") ||
  sessionStorage.getItem("zethAuthToken");

const clearAuth = () => {
  localStorage.removeItem("zethAuthToken");
  localStorage.removeItem("zethUser");
  sessionStorage.removeItem("zethAuthToken");
  sessionStorage.removeItem("zethUser");
};

const parseJson = async (response) => {
  const contentType = response.headers.get("content-type");
  return contentType?.includes("application/json")
    ? response.json().catch(() => ({}))
    : {};
};

function MasterDataManagement() {
  const [tab, setTab] = useState("professions");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);

  const [professions, setProfessions] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [selectedProfessionId, setSelectedProfessionId] = useState("");

  const [professionForm, setProfessionForm] = useState(null);
  const [specialtyForm, setSpecialtyForm] = useState(null);

  const request = useCallback(async (url, options = {}) => {
    const token = getToken();

    if (!token) {
      throw new Error("Authentication required.");
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await parseJson(response);

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error(data.message || "Administrator access required.");
    }

    if (!response.ok) {
      throw new Error(data.message || "Request failed.");
    }

    return data;
  }, []);

  const loadProfessions = useCallback(async () => {
    const data = await request(
      `${MASTER_DATA_URL}/professions?includeInactive=${includeInactive}`
    );

    const list = Array.isArray(data.professions) ? data.professions : [];
    setProfessions(list);

    setSelectedProfessionId((current) => {
      if (current && list.some((item) => String(item.id) === String(current))) {
        return current;
      }
      return list[0] ? String(list[0].id) : "";
    });
  }, [includeInactive, request]);

  const loadSpecialties = useCallback(
    async (professionId = selectedProfessionId) => {
      if (!professionId) {
        setSpecialties([]);
        return;
      }

      const data = await request(
        `${MASTER_DATA_URL}/specialties?professionId=${professionId}&includeInactive=${includeInactive}`
      );

      setSpecialties(Array.isArray(data.specialties) ? data.specialties : []);
    },
    [includeInactive, request, selectedProfessionId]
  );

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      await loadProfessions();
    } catch (loadError) {
      setError(loadError.message || "Unable to load master data.");
    } finally {
      setLoading(false);
    }
  }, [loadProfessions]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (tab === "specialties" && selectedProfessionId) {
      loadSpecialties(selectedProfessionId).catch((loadError) => {
        setError(loadError.message || "Unable to load specialties.");
      });
    }
  }, [tab, selectedProfessionId, loadSpecialties]);

  const normalizedSearch = search.trim().toLowerCase();

  const visibleProfessions = useMemo(() => {
    if (!normalizedSearch) return professions;

    return professions.filter((item) =>
      [item.name, item.code].some((value) =>
        String(value || "").toLowerCase().includes(normalizedSearch)
      )
    );
  }, [professions, normalizedSearch]);

  const visibleSpecialties = useMemo(() => {
    if (!normalizedSearch) return specialties;

    return specialties.filter((item) =>
      [item.name, item.profession_name, item.profession_code].some((value) =>
        String(value || "").toLowerCase().includes(normalizedSearch)
      )
    );
  }, [specialties, normalizedSearch]);

  const selectedProfession = professions.find(
    (item) => String(item.id) === String(selectedProfessionId)
  );

  const totalSpecialties = professions.reduce(
    (sum, item) => sum + Number(item.specialty_count || 0),
    0
  );

  const clearMessages = () => {
    setError("");
    setMessage("");
  };

  const refresh = async () => {
  try {
    setRefreshing(true);
    clearMessages();

    await loadProfessions();

    if (
      tab === "specialties" &&
      selectedProfessionId
    ) {
      await loadSpecialties(
        selectedProfessionId
      );
    }

    setMessage(
      "Master data refreshed."
    );
  } catch (refreshError) {
    setError(
      refreshError.message ||
        "Unable to refresh data."
    );
  } finally {
    setRefreshing(false);
  }
};

  const saveProfession = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      clearMessages();

      const payload = {
        name: professionForm.name.trim(),
        code: professionForm.code.trim().toUpperCase(),
        isActive: professionForm.isActive,
      };

      if (!payload.name || !payload.code) {
        throw new Error("Profession name and code are required.");
      }

      const editing = Boolean(professionForm.id);
      const data = await request(
        editing
          ? `${MASTER_DATA_URL}/professions/${professionForm.id}`
          : `${MASTER_DATA_URL}/professions`,
        {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );

      setProfessionForm(null);
      setMessage(data.message || "Profession saved successfully.");
      await loadProfessions();
    } catch (saveError) {
      setError(saveError.message || "Unable to save profession.");
    } finally {
      setSaving(false);
    }
  };

  const saveSpecialty = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      clearMessages();

      const payload = {
        professionId: Number(specialtyForm.professionId),
        name: specialtyForm.name.trim(),
        displayOrder: Number(specialtyForm.displayOrder),
        isActive: specialtyForm.isActive,
      };

      if (!payload.professionId || !payload.name) {
        throw new Error("Profession and specialty name are required.");
      }

      const editing = Boolean(specialtyForm.id);
      const data = await request(
        editing
          ? `${MASTER_DATA_URL}/specialties/${specialtyForm.id}`
          : `${MASTER_DATA_URL}/specialties`,
        {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );

      setSpecialtyForm(null);
      setSelectedProfessionId(String(payload.professionId));
      setMessage(data.message || "Specialty saved successfully.");

      await Promise.all([
        loadProfessions(),
        loadSpecialties(payload.professionId),
      ]);
    } catch (saveError) {
      setError(saveError.message || "Unable to save specialty.");
    } finally {
      setSaving(false);
    }
  };

  const toggleProfession = async (profession) => {
    try {
      clearMessages();
      const data = await request(
        `${MASTER_DATA_URL}/professions/${profession.id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ isActive: !profession.is_active }),
        }
      );
      setMessage(data.message || "Profession status updated.");
      await loadProfessions();
    } catch (statusError) {
      setError(statusError.message || "Unable to update profession status.");
    }
  };

  const toggleSpecialty = async (specialty) => {
    try {
      clearMessages();
      const data = await request(
        `${MASTER_DATA_URL}/specialties/${specialty.id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ isActive: !specialty.is_active }),
        }
      );
      setMessage(data.message || "Specialty status updated.");
      await Promise.all([
        loadProfessions(),
        loadSpecialties(selectedProfessionId),
      ]);
    } catch (statusError) {
      setError(statusError.message || "Unable to update specialty status.");
    }
  };

  if (loading) {
    return (
      <PageLoader
        title="Master Data"
        message="Loading professions and specialties..."
      />
    );
  }

  return (
    <main className="master-data-page">
      <header className="master-data-header">
        <div>
          <p className="master-data-eyebrow">Administration</p>
          <h1>Master Data Management</h1>
          <p>
            Manage the professions and profession-specific specialties used
            throughout Zeth Health.
          </p>
        </div>

        <button
          type="button"
          className="master-data-refresh-button"
          onClick={refresh}
          disabled={refreshing}
        >
          <RefreshCw size={18} className={refreshing ? "is-spinning" : ""} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      <section className="master-data-summary-grid">
        <SummaryCard
          icon={<BriefcaseMedical size={22} />}
          value={professions.length}
          label="Total Professions"
        />
        <SummaryCard
          icon={<CheckCircle2 size={22} />}
          value={professions.filter((item) => item.is_active).length}
          label="Active Professions"
        />
        <SummaryCard
          icon={<Stethoscope size={22} />}
          value={totalSpecialties}
          label="Total Specialties"
        />
      </section>

      {(error || message) && (
        <div
          className={`master-data-message ${error ? "error" : "success"}`}
          role={error ? "alert" : "status"}
        >
          <span>{error || message}</span>
          <button type="button" onClick={clearMessages} aria-label="Dismiss">
            <X size={18} />
          </button>
        </div>
      )}

      <section className="master-data-panel">
        <div className="master-data-toolbar">
          <div className="master-data-tabs">
            <button
              type="button"
              className={tab === "professions" ? "active" : ""}
              onClick={() => {
                setTab("professions");
                setSearch("");
              }}
            >
              <BriefcaseMedical size={18} /> Professions
            </button>

            <button
              type="button"
              className={tab === "specialties" ? "active" : ""}
              onClick={() => {
                setTab("specialties");
                setSearch("");
              }}
            >
              <Layers3 size={18} /> Specialties
            </button>
          </div>

          <div className="master-data-toolbar-actions">
            {tab === "specialties" && (
              <select
                className="master-data-profession-filter"
                value={selectedProfessionId}
                onChange={(event) =>
                  setSelectedProfessionId(event.target.value)
                }
              >
                {professions.map((profession) => (
                  <option key={profession.id} value={profession.id}>
                    {profession.name} ({profession.code})
                  </option>
                ))}
              </select>
            )}

            <label className="master-data-search">
              <Search size={18} />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`Search ${tab}...`}
              />
            </label>

            <label className="master-data-toggle">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(event) => setIncludeInactive(event.target.checked)}
              />
              Show inactive
            </label>

            <button
              type="button"
              className="master-data-primary-button"
              onClick={() => {
                clearMessages();
                if (tab === "professions") {
                  setProfessionForm({ ...EMPTY_PROFESSION });
                } else {
                  setSpecialtyForm({
                    ...EMPTY_SPECIALTY,
                    professionId: selectedProfessionId,
                    displayOrder: String(specialties.length + 1),
                  });
                }
              }}
            >
              <CirclePlus size={18} />
              {tab === "professions" ? "Add Profession" : "Add Specialty"}
            </button>
          </div>
        </div>

        {tab === "professions" ? (
          <DataTable
            headers={["Profession", "Code", "Specialties", "Status", "Actions"]}
            empty={visibleProfessions.length === 0}
            emptyText="No professions match your search."
          >
            {visibleProfessions.map((profession) => (
              <tr key={profession.id}>
                <td>
                  <NameCell
                    icon={<BriefcaseMedical size={18} />}
                    title={profession.name}
                    subtitle={`ID: ${profession.id}`}
                  />
                </td>
                <td>
                  <span className="master-data-code-badge">
                    {profession.code}
                  </span>
                </td>
                <td>{profession.specialty_count}</td>
                <td>
                  <StatusBadge active={profession.is_active} />
                </td>
                <td>
                  <RowActions
                    active={profession.is_active}
                    onEdit={() =>
                      setProfessionForm({
                        id: profession.id,
                        name: profession.name,
                        code: profession.code,
                        isActive: Boolean(profession.is_active),
                      })
                    }
                    onToggle={() => toggleProfession(profession)}
                  />
                </td>
              </tr>
            ))}
          </DataTable>
        ) : (
          <>
            <div className="master-data-context-row">
              <div>
                <span>Selected profession</span>
                <strong>
                  {selectedProfession
                    ? `${selectedProfession.name} (${selectedProfession.code})`
                    : "None selected"}
                </strong>
              </div>
              <span>{visibleSpecialties.length} specialties</span>
            </div>

            <DataTable
              headers={["Specialty", "Profession", "Order", "Status", "Actions"]}
              empty={visibleSpecialties.length === 0}
              emptyText="No specialties are available for this profession."
            >
              {visibleSpecialties.map((specialty) => (
                <tr key={specialty.id}>
                  <td>
                    <NameCell
                      specialty
                      icon={<Stethoscope size={18} />}
                      title={specialty.name}
                      subtitle={`ID: ${specialty.id}`}
                    />
                  </td>
                  <td>{specialty.profession_name}</td>
                  <td>{specialty.display_order}</td>
                  <td>
                    <StatusBadge active={specialty.is_active} />
                  </td>
                  <td>
                    <RowActions
                      active={specialty.is_active}
                      onEdit={() =>
                        setSpecialtyForm({
                          id: specialty.id,
                          professionId: String(specialty.profession_id),
                          name: specialty.name,
                          displayOrder: String(specialty.display_order || 1),
                          isActive: Boolean(specialty.is_active),
                        })
                      }
                      onToggle={() => toggleSpecialty(specialty)}
                    />
                  </td>
                </tr>
              ))}
            </DataTable>
          </>
        )}
      </section>

      {professionForm && (
        <Modal title={professionForm.id ? "Edit Profession" : "Add Profession"} onClose={() => setProfessionForm(null)}>
          <form className="master-data-form" onSubmit={saveProfession}>
            <label>
              Profession name
              <input
                type="text"
                value={professionForm.name}
                onChange={(event) =>
                  setProfessionForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </label>

            <label>
              Profession code
              <input
                type="text"
                value={professionForm.code}
                onChange={(event) =>
                  setProfessionForm((current) => ({
                    ...current,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                maxLength={12}
                required
              />
            </label>

            <CheckboxField
              label="Profession is active"
              checked={professionForm.isActive}
              onChange={(checked) =>
                setProfessionForm((current) => ({
                  ...current,
                  isActive: checked,
                }))
              }
            />

            <FormActions
              saving={saving}
              saveLabel="Save Profession"
              onCancel={() => setProfessionForm(null)}
            />
          </form>
        </Modal>
      )}

      {specialtyForm && (
        <Modal title={specialtyForm.id ? "Edit Specialty" : "Add Specialty"} onClose={() => setSpecialtyForm(null)}>
          <form className="master-data-form" onSubmit={saveSpecialty}>
            <label>
              Profession
              <select
                value={specialtyForm.professionId}
                onChange={(event) =>
                  setSpecialtyForm((current) => ({
                    ...current,
                    professionId: event.target.value,
                  }))
                }
                required
              >
                <option value="">Select profession</option>
                {professions.map((profession) => (
                  <option key={profession.id} value={profession.id}>
                    {profession.name} ({profession.code})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Specialty name
              <input
                type="text"
                value={specialtyForm.name}
                onChange={(event) =>
                  setSpecialtyForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </label>

            <label>
              Display order
              <input
                type="number"
                min="1"
                value={specialtyForm.displayOrder}
                onChange={(event) =>
                  setSpecialtyForm((current) => ({
                    ...current,
                    displayOrder: event.target.value,
                  }))
                }
                required
              />
            </label>

            <CheckboxField
              label="Specialty is active"
              checked={specialtyForm.isActive}
              onChange={(checked) =>
                setSpecialtyForm((current) => ({
                  ...current,
                  isActive: checked,
                }))
              }
            />

            <FormActions
              saving={saving}
              saveLabel="Save Specialty"
              onCancel={() => setSpecialtyForm(null)}
            />
          </form>
        </Modal>
      )}
    </main>
  );
}

function SummaryCard({ icon, value, label }) {
  return (
    <article className="master-data-summary-card">
      <span>{icon}</span>
      <div>
        <strong>{value}</strong>
        <small>{label}</small>
      </div>
    </article>
  );
}

function DataTable({ headers, children, empty, emptyText }) {
  return (
    <div className="master-data-table-wrapper">
      <table className="master-data-table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <div className="master-data-empty">{emptyText}</div>}
    </div>
  );
}

function NameCell({ icon, title, subtitle, specialty = false }) {
  return (
    <div className="master-data-name-cell">
      <span className={specialty ? "specialty" : ""}>{icon}</span>
      <div>
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span className={`master-data-status ${active ? "active" : "inactive"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function RowActions({ active, onEdit, onToggle }) {
  return (
    <div className="master-data-row-actions">
      <button type="button" onClick={onEdit}>
        <Edit3 size={16} /> Edit
      </button>
      <button
        type="button"
        className={active ? "danger" : "success"}
        onClick={onToggle}
      >
        <Power size={16} /> {active ? "Deactivate" : "Activate"}
      </button>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div
      className="master-data-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="master-data-modal" role="dialog" aria-modal="true">
        <header>
          <div>
            <p>Master Data</p>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label className="master-data-checkbox-field">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function FormActions({ saving, saveLabel, onCancel }) {
  return (
    <footer className="master-data-form-actions">
      <button
        type="button"
        className="master-data-secondary-button"
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="master-data-primary-button"
        disabled={saving}
      >
        {saving ? "Saving..." : saveLabel}
      </button>
    </footer>
  );
}

export default MasterDataManagement;
